import React, { useState } from 'react';
import './CountryFlag.css';

const ISO2 = /^[A-Za-z]{2}$/;

/**
 * Render a country flag from an ISO 3166-1 alpha-2 code as a real image.
 *
 * We deliberately use flag *images* (flagcdn) rather than emoji: Windows does
 * not render flag emoji and shows the bare letters ("SE") instead, which looked
 * like a bug. Images render identically across Windows/macOS/Linux. A non-ISO
 * label (e.g. "Nordic") or a failed image load falls back to a small text chip,
 * and an empty code renders nothing so callers can place it unconditionally.
 *
 * @param {object} props - Component props.
 * @param {string} props.code - ISO-2 country code (or a free-form region label).
 * @param {('sm'|'md'|'lg')} [props.size] - Flag size tier (default 'sm').
 * @param {string} [props.className] - Extra class appended to the flag.
 */
export default function CountryFlag({ code, size = 'sm', className = '' }) {
  const [failed, setFailed] = useState(false);
  const raw = (code || '').trim();
  if (!raw) return null;

  if (!ISO2.test(raw) || failed) {
    return <span className={`country-flag-text ${className}`.trim()}>{raw.toUpperCase()}</span>;
  }

  const cc = raw.toLowerCase();
  const up = raw.toUpperCase();
  return (
    <img
      className={`country-flag country-flag-${size} ${className}`.trim()}
      src={`https://flagcdn.com/w40/${cc}.png`}
      srcSet={`https://flagcdn.com/w80/${cc}.png 2x`}
      alt={up}
      title={up}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}
